import type { InputState } from './InputSystem';

const RING_RADIUS = 60;   // px — ring is 120×120, half = 60
const MAX_TRAVEL  = 50;   // px — max stick displacement from origin
const FIRE_DEAD   = 14;   // px — right stick horizontal travel before auto-fire

interface Stick {
  active:    boolean;
  id:        number;   // touch identifier
  originX:   number;
  originY:   number;
  currX:     number;
  currY:     number;
}

function emptyStick(): Stick {
  return { active: false, id: -1, originX: 0, originY: 0, currX: 0, currY: 0 };
}

export class TouchInput {
  private left  = emptyStick();
  private right = emptyStick();

  private leftRing:  HTMLElement;
  private leftStick: HTMLElement;
  private rightRing:  HTMLElement;
  private rightStick: HTMLElement;

  constructor() {
    this.leftRing   = document.getElementById('joy-left-ring')!;
    this.leftStick  = document.getElementById('joy-left-stick')!;
    this.rightRing  = document.getElementById('joy-right-ring')!;
    this.rightStick = document.getElementById('joy-right-stick')!;

    // Listen on the canvas — the DOM's hit-test naturally keeps joystick events
    // off the HUD buttons and upgrade-menu overlay (they sit above the canvas).
    const canvas = document.getElementById('application-canvas')!;
    canvas.addEventListener('touchstart',  this.onStart.bind(this),  { passive: false });
    canvas.addEventListener('touchmove',   this.onMove.bind(this),   { passive: false });
    canvas.addEventListener('touchend',    this.onEnd.bind(this),    { passive: false });
    canvas.addEventListener('touchcancel', this.onEnd.bind(this),    { passive: false });
  }

  // ── Touch handlers ──────────────────────────────────────────

  private onStart(e: TouchEvent) {
    e.preventDefault();
    const midX = window.innerWidth / 2;

    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];

      if (t.clientX < midX) {
        if (this.left.active) continue;
        this.left = { active: true, id: t.identifier, originX: t.clientX, originY: t.clientY, currX: t.clientX, currY: t.clientY };
        this.placeRing(this.leftRing, this.leftStick, t.clientX, t.clientY);
      } else {
        if (this.right.active) continue;
        this.right = { active: true, id: t.identifier, originX: t.clientX, originY: t.clientY, currX: t.clientX, currY: t.clientY };
        this.placeRing(this.rightRing, this.rightStick, t.clientX, t.clientY);
      }
    }
  }

  private onMove(e: TouchEvent) {
    e.preventDefault();
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (this.left.active  && t.identifier === this.left.id)  { this.left.currX  = t.clientX; this.left.currY  = t.clientY; this.animStick(this.leftStick,  t.clientX - this.left.originX,  t.clientY - this.left.originY);  }
      if (this.right.active && t.identifier === this.right.id) { this.right.currX = t.clientX; this.right.currY = t.clientY; this.animStick(this.rightStick, t.clientX - this.right.originX, t.clientY - this.right.originY); }
    }
  }

  private onEnd(e: TouchEvent) {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (this.left.active  && t.identifier === this.left.id)  { this.left  = emptyStick(); this.leftRing.style.opacity  = '0'; this.animStick(this.leftStick,  0, 0); }
      if (this.right.active && t.identifier === this.right.id) { this.right = emptyStick(); this.rightRing.style.opacity = '0'; this.animStick(this.rightStick, 0, 0); }
    }
  }

  // ── Visual helpers ──────────────────────────────────────────

  private placeRing(ring: HTMLElement, stick: HTMLElement, x: number, y: number) {
    // Clamp so the ring stays fully on screen
    const cx = Math.max(RING_RADIUS, Math.min(window.innerWidth  - RING_RADIUS, x));
    const cy = Math.max(RING_RADIUS, Math.min(window.innerHeight - RING_RADIUS, y));
    ring.style.left    = `${cx - RING_RADIUS}px`;
    ring.style.top     = `${cy - RING_RADIUS}px`;
    ring.style.opacity = '1';
    // Reset stick to center
    stick.style.left      = `${RING_RADIUS}px`;
    stick.style.top       = `${RING_RADIUS}px`;
    stick.style.transform = 'translate(-50%, -50%)';
  }

  private animStick(stick: HTMLElement, rawDx: number, rawDy: number) {
    const dist   = Math.sqrt(rawDx * rawDx + rawDy * rawDy);
    const clamp  = Math.min(dist, MAX_TRAVEL);
    const ox = dist > 0 ? (rawDx / dist) * clamp : 0;
    const oy = dist > 0 ? (rawDy / dist) * clamp : 0;
    stick.style.left      = `${RING_RADIUS + ox}px`;
    stick.style.top       = `${RING_RADIUS + oy}px`;
    stick.style.transform = 'translate(-50%, -50%)';
  }

  // ── Input state ─────────────────────────────────────────────

  capture(seq: number): InputState {
    let dx = 0, dz = 0, turn = 0, firing = false;

    // Left stick → movement (screen-right = game-dx, screen-down = game-dz)
    if (this.left.active) {
      const rdx = this.left.currX - this.left.originX;
      const rdy = this.left.currY - this.left.originY;
      const len = Math.sqrt(rdx * rdx + rdy * rdy);
      if (len > 6) {
        const norm = Math.min(len, MAX_TRAVEL) / MAX_TRAVEL;
        dx = (rdx / len) * norm;
        dz = (rdy / len) * norm;
      }
    }

    // Right stick → turn (horizontal only) + auto-fire when deflected past dead zone
    if (this.right.active) {
      const rdx = this.right.currX - this.right.originX;
      const rdy = this.right.currY - this.right.originY;
      const len = Math.sqrt(rdx * rdx + rdy * rdy);
      if (len > 6) {
        const norm = Math.min(len, MAX_TRAVEL) / MAX_TRAVEL;
        turn = (rdx / len) * norm;
        firing = Math.abs(rdx) > FIRE_DEAD;
      }
    }

    return { dx, dz, turn, firing, orbitV: 0, seq, mouseX: 0, mouseY: 0 };
  }

  static detect(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
}
